import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageShell, SkeletonCard, ErrorMessage,
} from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { getRiskColors, getRiskLabel } from '../utils/riskUtils';
import {
  formatDate, getWeekStart, getWeekRange, addWeeks, formatWeekRange,
} from '../utils/dateUtils';
import { FloatThresholds } from '../utils/riskUtils';
import { Activity, Project } from '../types';
import { useWeeklyData } from '../hooks/useWeeklyData';
import { trackEvent } from '../utils/clarity';
import WeeklyGrid from '../components/WeeklyGrid';
import { SectionFlag, WeeklyGridSection, BatchSaveSummary } from '../components/WeeklyGrid';
import FloatPathGrid from '../components/FloatPathGrid';

/* ── Date helpers ── */
function toStr(d: string | undefined | null): string { return (d ?? '').substring(0, 10); }

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Sort by the display Finish/Start date (Finish_Date/Start_Date — computed by
// calculateDisplayDates(), status-dependent) rather than the raw P6 planned_finish/
// planned_start, so section membership/order matches what the UI actually shows.
function sortByFinishDate(a: Activity, b: Activity): number {
  const as = toStr(a.finish_date);
  const bs = toStr(b.finish_date);
  return as < bs ? -1 : as > bs ? 1 : 0;
}

function sortByStartDate(a: Activity, b: Activity): number {
  const as = toStr(a.start_date);
  const bs = toStr(b.start_date);
  return as < bs ? -1 : as > bs ? 1 : 0;
}

/* ── Health summary bar ── */
const HealthBar: React.FC<{
  weekActivities: Activity[];
  allActivities: Activity[];
  project: Project | undefined;
  weekStartStr: string;
  weekEndStr: string;
  todayStr: string;
}> = ({ weekActivities, allActivities, project, weekStartStr, weekEndStr, todayStr }) => {
  const criticalCount = allActivities.filter(a => a.is_critical === true).length;
  // NOT switched to display dates — deliberately different semantic from the section
  // filters below. "Slipping" asks whether the activity ran/is running behind its
  // ORIGINAL P6 plan (planned_finish), not which date-range window it currently falls
  // into. Comparing actual/current-forecast against planned_finish is the whole point of
  // this metric; switching it to finish_date (itself status-derived from actual/forecast
  // dates) would make it compare actual-vs-actual and collapse the signal to ~0.
  const slippingCount = allActivities.filter(a =>
    (!a.actual_finish && toStr(a.planned_finish) !== '' && toStr(a.planned_finish) < todayStr) ||
    (!!a.actual_finish && toStr(a.actual_finish) > toStr(a.planned_finish))
  ).length;
  const thisWeekCount = weekActivities.length;
  const minFloat = allActivities.length > 0
    ? Math.min(...allActivities.filter(a => a.total_float != null).map(a => a.total_float))
    : null;
  const riskColors = project ? getRiskColors(project.risk_status) : null;

  return (
    <div className="bg-tint dark:bg-green-900/10 border-b border-accent/20 px-4 py-2 flex flex-wrap items-center gap-2">
      {riskColors && project && (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${riskColors.bgClass} ${riskColors.textClass}`}>
          {getRiskLabel(project.risk_status)}
        </span>
      )}
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${criticalCount > 0 ? 'bg-crit-bg text-crit-text' : 'bg-tint text-accent'}`}>
        {criticalCount} critical
      </span>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${slippingCount > 0 ? 'bg-watch-bg text-watch-text' : 'bg-tint text-accent'}`}>
        {slippingCount} slipping
      </span>
      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
        {thisWeekCount} this week
      </span>
      <span className="ml-auto text-xs text-accent dark:text-green-400 font-medium hidden sm:block">
        {minFloat != null && minFloat !== Infinity && `Min float: ${minFloat}d`}
        {project?.target_completion && ` · Target: ${formatDate(project.target_completion)}`}
      </span>
    </div>
  );
};

/* ── Main screen ── */
const Weekly: React.FC = () => {
  const { project_id } = useParams<{ project_id: string }>();
  const { projects, setSelectedProject, setProjects } = useApp();
  const { userEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const flowClient = useFlowClient();

  const { activities, loading, error, load, updateActivity } = useWeeklyData(userEmail, project_id);

  const [tab, setTab] = useState<'week' | 'all' | 'floatpath'>('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const [projectSearch, setProjectSearch] = useState<string | null>(null);

  // Dismissible batch-save summary popup (Weekly Lookahead's multi-edit Save only) — unlike
  // savePopup below, this does NOT auto-dismiss, since it can carry a completion-date shift
  // and the project's full current critical-activities list the PM should actually read
  // before it disappears.
  const [batchSummary, setBatchSummary] = useState<{
    count: number;
    completionChanged: boolean;
    completionBefore: string | null;
    completionAfter: string | null;
    daysDiff: number;
    criticalPath: { activity_id: string; activity_name: string; is_new: boolean }[];
  } | null>(null);

  // Centered save-status popup — 'saving' persists until explicitly replaced by a
  // success/error call (no auto-dismiss while the request is in flight); success/error
  // auto-dismiss after a delay.
  const [savePopup, setSavePopup] = useState<{ status: 'saving' | 'success' | 'error'; message: string } | null>(null);
  const popupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showSavePopup = useCallback((status: 'saving' | 'success' | 'error', message: string) => {
    if (popupTimer.current) clearTimeout(popupTimer.current);
    setSavePopup({ status, message });
    if (status !== 'saving') {
      popupTimer.current = setTimeout(() => setSavePopup(null), status === 'error' ? 4000 : 2000);
    }
  }, []);
  useEffect(() => () => { if (popupTimer.current) clearTimeout(popupTimer.current); }, []);
  useEffect(() => { trackEvent('lookahead_viewed'); }, []);

  // All tab filter state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // All tab sort state
  const [sortKey, setSortKey] = useState('planned_finish');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');


  const project = projects.find(p => p.id === project_id);
  useEffect(() => { if (project) setSelectedProject(project); }, [project, setSelectedProject]);

  const thresholds: FloatThresholds = {
    critical: project?.critical_threshold ?? 0,
    watch: project?.watch_threshold ?? 5,
    is_distressed: project?.distressed_mode === 'Yes',
    negative_float_count: project?.negative_float_count ?? 0,
    total_activities: activities.length,
    negative_pct: activities.length > 0 ? ((project?.negative_float_count ?? 0) / activities.length) * 100 : 0,
  };

  // Load on mount / project change
  useEffect(() => { load(); }, [load]);

  // Cross-page deep link — Dashboard's "View all N critical activities" link (and any
  // other future link) lands here via ?tab=all&critical=1 rather than component state,
  // since tab/statusFilter live only in this component and can't be set from elsewhere.
  // Cleared immediately after applying so back/refresh doesn't re-force the filter.
  useEffect(() => {
    if (searchParams.get('tab') === 'all') setTab('all');
    if (searchParams.get('critical') === '1') setStatusFilter('critical');
    if (searchParams.size > 0) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── Week bounds ──────────────────────────────────────────────────────────
  const weekStart = getWeekStart(addWeeks(new Date(), weekOffset));
  const { start, end } = getWeekRange(weekStart);
  const weekLabel = formatWeekRange(start, end);
  const weekStartStr = localDateStr(start);
  const weekEndStr = localDateStr(new Date(end.getFullYear(), end.getMonth(), end.getDate()));
  const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
  const todayStr = localDateStr(todayDate);
  const twoDaysOut = new Date(todayDate); twoDaysOut.setDate(twoDaysOut.getDate() + 2);
  const todayPlus2Str = localDateStr(twoDaysOut);

  // ── Section filtering ────────────────────────────────────────────────────
  // Uses the display Finish_Date/Start_Date fields (start_date/finish_date on Activity —
  // computed by calculateDisplayDates(), status-dependent: e.g. Complete shows actual
  // dates, In Progress shows early_finish) rather than raw planned_start/planned_finish,
  // so section membership matches what the 2 Week Lookahead report and the grid's own
  // Start/Finish columns show — one date logic, not two. See business-rules.md.
  const finishingBase = activities
    .filter(a =>
      !toStr(a.actual_finish) &&
      toStr(a.finish_date) >= weekStartStr &&
      toStr(a.finish_date) <= weekEndStr
    )
    .sort(sortByFinishDate);

  const finishingIds = new Set(finishingBase.map(a => a.activity_id));

  const startingSection = activities
    .filter(a =>
      !toStr(a.actual_start) &&
      toStr(a.start_date) >= weekStartStr &&
      toStr(a.start_date) <= weekEndStr &&
      !finishingIds.has(a.activity_id)
    )
    .sort(sortByStartDate);

  // Note: no longer excludes a separate "overdueIds" set (Overdue section was removed —
  // see business-rules.md). A late-running In Progress activity whose finish_date is
  // already before weekStartStr fails the Finishing window check below, so it naturally
  // falls into Ongoing instead — it doesn't disappear from the view.
  const ongoingSection = activities
    .filter(a =>
      !!toStr(a.actual_start) &&
      !toStr(a.actual_finish) &&
      !(toStr(a.finish_date) >= weekStartStr && toStr(a.finish_date) <= weekEndStr) &&
      !finishingIds.has(a.activity_id)
    )
    .sort(sortByFinishDate);

  // Per-section flag computers — same display-date fields as the section filters above.
  const startingFlag = (a: Activity): SectionFlag =>
    toStr(a.start_date) < todayStr
      ? { color: 'red', label: 'No actual start' }
      : null;

  const ongoingFlag = (_a: Activity): SectionFlag => null;

  const finishingFlag = (a: Activity): SectionFlag => {
    if (!toStr(a.actual_start)) return { color: 'red', label: 'Not started — due to finish' };
    if ((a.pct_complete ?? 0) < 50 && toStr(a.finish_date) <= todayPlus2Str) {
      return { color: 'amber', label: 'Behind pace' };
    }
    return null;
  };

  const sections: WeeklyGridSection[] = [
    { label: 'Starting this week', activities: startingSection, danger: false, getFlag: startingFlag },
    { label: 'Ongoing this week', activities: ongoingSection, danger: false, getFlag: ongoingFlag },
    { label: 'Finishing this week', activities: finishingBase, danger: false, getFlag: finishingFlag },
  ];

  const totalWeekActivities = startingSection.length + ongoingSection.length + finishingBase.length;
  const isWeekEmpty = totalWeekActivities === 0;

  // ── All tab filtering ────────────────────────────────────────────────────
  const filteredAll = activities
    .filter(a => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!a.activity_name.toLowerCase().includes(q) && !a.activity_id.toLowerCase().includes(q)) return false;
      }
      if (statusFilter === 'critical') {
        // Critical is a float-tier concept (is_critical, the worst-float/longest-path
        // definition — same field the Watch/Critical badges already use elsewhere),
        // not an activity_status value, so it needs its own branch here.
        if (a.is_critical !== true) return false;
      } else if (statusFilter !== 'all') {
        const s = (a.activity_status ?? '').toLowerCase();
        if (s !== statusFilter.toLowerCase()) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const NUMERIC_KEYS = ['total_float', 'pct_complete'];
      const ra = a as unknown as Record<string, unknown>;
      const rb = b as unknown as Record<string, unknown>;
      let cmp: number;
      if (NUMERIC_KEYS.includes(sortKey)) {
        const an = Number(ra[sortKey] ?? 0);
        const bn = Number(rb[sortKey] ?? 0);
        cmp = an - bn;
      } else {
        const av = String(ra[sortKey] ?? '');
        const bv = String(rb[sortKey] ?? '');
        cmp = av < bv ? -1 : av > bv ? 1 : 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // ── Toggle expand ────────────────────────────────────────────────────────
  const handleToggle = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  // ── Submit success handler ────────────────────────────────────────────────
  const activityNames: Record<string, string> = Object.fromEntries(
    activities.map(a => [a.activity_id, a.activity_name])
  );

  const handleSubmitSuccess = useCallback((activityId: string, changes: Partial<Activity>, _originals: Partial<Activity>) => {
    // Optimistic patch for instant feedback on the edited row, immediately followed by a
    // full re-fetch — Submit can rewrite ES/EF/LS/LF/float/is_critical/float_path for every
    // activity project-wide (not just this one or its successors), so local state alone
    // would leave every other activity stale across all three tabs (they share this same
    // `activities` array from useWeeklyData) until a manual page refresh.
    updateActivity(activityId, changes);
    setExpandedId(null);
    setFlashId(activityId);
    setTimeout(() => setFlashId(null), 500);
    showSavePopup('success', 'Changes saved');
    load(true);
  }, [updateActivity, load, showSavePopup]);

  const handleSubmitError = useCallback((message: string) => {
    showSavePopup('error', message || 'Failed to save changes.');
  }, [showSavePopup]);

  const handleSubmitStart = useCallback(() => {
    showSavePopup('saving', 'Saving changes...');
  }, [showSavePopup]);

  // ── Weekly Lookahead batch-save success handler (Part 1) ───────────────────
  // Fired once after the grid's single Save button submits every pending edit across
  // every edited row in one request/CPM run. Same optimistic-patch + full-refresh
  // pattern as handleSubmitSuccess above, just applied to N activities instead of one —
  // plus the batch-summary popup and the AppContext.projects refresh (so Dashboard/
  // Portfolio pick up the new completion date without a manual page reload, same as
  // ExpandedRow's single-edit flow already does).
  const handleBatchSubmitSuccess = useCallback((activityIds: string[], updatedActivities: Record<string, any>[], summary: BatchSaveSummary) => {
    const byId = new Map(updatedActivities.map(ua => [String(ua.activity_id), ua]));
    for (const activityId of activityIds) {
      const ua = byId.get(activityId);
      if (!ua) continue;
      updateActivity(activityId, {
        planned_start: ua.start_date,
        planned_finish: ua.end_date,
        actual_start: ua.act_start_date ?? null,
        actual_finish: ua.act_end_date ?? null,
        forecast_start: ua.forecast_start_date ?? null,
        forecast_finish: ua.forecast_end_date ?? null,
        start_date: ua.Start_Date ?? null,
        finish_date: ua.Finish_Date ?? null,
        total_float: ua.float_days ?? 0,
        pct_complete: ua.complete_pct ?? 0,
        activity_status: ua.activity_status,
      });
    }
    setSavePopup(null);
    setBatchSummary({
      count: activityIds.length,
      completionChanged: summary.completion_date_changed,
      completionBefore: summary.completion_date_before,
      completionAfter: summary.completion_date,
      daysDiff: summary.completion_date_days_diff,
      criticalPath: summary.critical_path,
    });
    load(true);

    // Refresh the app-wide project list so Dashboard/Portfolio show the new completion
    // date immediately — non-fatal, mirrors ExpandedRow.tsx's identical refresh.
    if (userEmail) {
      flowClient.projects(userEmail)
        .then(refreshed => { if (Array.isArray(refreshed.projects)) setProjects(refreshed.projects); })
        .catch(() => { /* non-fatal — the batch save already succeeded */ });
    }
  }, [updateActivity, load, userEmail, flowClient, setProjects]);

  const closeBatchSummary = useCallback(() => setBatchSummary(null), []);

  // Click-through from the popup's critical-path list — jump to the All Activities tab
  // with that activity's ID pre-filled in the existing search box (the app's only
  // "find one activity" mechanism; no dedicated jump-to-activity feature exists elsewhere).
  const handleCriticalActivityClick = useCallback((activityId: string) => {
    setBatchSummary(null);
    setStatusFilter('all');
    setSearchText(activityId);
    setTab('all');
  }, []);

  // Click-through from "View all N critical activities" — All Activities tab, filtered to
  // the new "Critical only" status option. Same page/component, so just flip local state
  // directly rather than a full navigation (Dashboard's equivalent link, a different page,
  // uses the ?tab=all&critical=1 deep link above instead).
  const handleViewAllCritical = useCallback(() => {
    setBatchSummary(null);
    setSearchText('');
    setStatusFilter('critical');
    setTab('all');
  }, []);

  // ── Jump to date ─────────────────────────────────────────────────────────
  const handleJumpDate = (dateStr: string) => {
    if (!dateStr) return;
    const [y, m, d] = dateStr.split('-').map(Number);
    const picked = new Date(y, m - 1, d);
    const pickedMonday = getWeekStart(picked);
    const thisMonday = getWeekStart(new Date());
    const diffMs = pickedMonday.getTime() - thisMonday.getTime();
    const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
    setWeekOffset(diffWeeks);
  };

  // ── Project switcher ─────────────────────────────────────────────────────
  const filteredProjects = projects.filter(p =>
    !projectSearch || p.project_name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const projectSwitcher = (
    <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <label className="text-xs text-muted dark:text-gray-400 whitespace-nowrap">Project:</label>
      <div className="relative flex-1 min-w-0 sm:flex-none">
        <input
          type="text"
          value={projectSearch !== null ? projectSearch : (project?.project_name ?? '')}
          onFocus={() => setProjectSearch('')}
          onChange={e => setProjectSearch(e.target.value)}
          onBlur={() => setTimeout(() => setProjectSearch(null), 150)}
          className="border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1 text-xs font-semibold bg-white dark:bg-gray-800 text-text dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent w-full sm:w-[240px] cursor-pointer transition-colors tap"
          placeholder="Search projects..."
        />
        {projectSearch !== null && filteredProjects.length > 0 && (
          <div className="absolute top-full left-0 z-50 mt-1 w-full sm:w-72 max-w-[calc(100vw-24px)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-52 overflow-y-auto">
            {filteredProjects.map(p => (
              <button
                key={p.id}
                onMouseDown={() => {
                  setSelectedProject(p);
                  navigate(`/project/${p.id}/lookahead`);
                  setProjectSearch(null);
                }}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  p.id === project_id
                    ? 'font-semibold text-accent dark:text-green-400'
                    : 'text-text dark:text-gray-100'
                }`}
              >
                {p.project_name}
              </button>
            ))}
          </div>
        )}
      </div>
      <span className="sm:ml-auto text-xs text-muted dark:text-gray-400 whitespace-nowrap">
        Data date: <span className="font-semibold text-text dark:text-gray-100">{formatDate(project?.data_date)}</span>
      </span>
    </div>
  );

  // ── Tab bar ───────────────────────────────────────────────────────────────
  const tabBar = (
    <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <button
        onClick={() => setTab('week')}
        className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap shrink-0 tap ${
          tab === 'week'
            ? 'border-b-2 border-accent text-accent dark:text-green-400 dark:border-green-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {/* Calendar icon */}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="sm:hidden">Weekly</span>
        <span className="hidden sm:inline">Weekly Lookahead</span>
      </button>
      <button
        onClick={() => setTab('all')}
        className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap shrink-0 tap ${
          tab === 'all'
            ? 'border-b-2 border-accent text-accent dark:text-green-400 dark:border-green-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {/* List icon */}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
        <span className="sm:hidden">All</span>
        <span className="hidden sm:inline">All activities</span>
        <span className="ml-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full">
          {activities.length}
        </span>
      </button>
      <button
        onClick={() => setTab('floatpath')}
        className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap shrink-0 tap ${
          tab === 'floatpath'
            ? 'border-b-2 border-accent text-accent dark:text-green-400 dark:border-green-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {/* Branch/route icon */}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="6" cy="18" r="2" />
          <circle cx="18" cy="12" r="2" />
          <path d="M6 8v8" />
          <path d="M8 6h4a4 4 0 0 1 4 4" />
          <path d="M8 18h4a4 4 0 0 0 4-4" />
        </svg>
        <span className="sm:hidden">Float path</span>
        <span className="hidden sm:inline">Float path view</span>
        <span className="ml-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full">
          {activities.length}
        </span>
      </button>
    </div>
  );

  // ── Week nav bar ──────────────────────────────────────────────────────────
  // `tap` = 44px min height on touch screens only (index.css); desktop keeps the compact size.
  const btnCls = 'inline-flex items-center justify-center border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1 text-xs bg-white dark:bg-gray-800 text-text dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors tap';

  const weekNavBar = (
    <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <button onClick={() => setWeekOffset(o => o - 1)} className={btnCls} aria-label="Previous week">
        ← Prev
      </button>
      <span className="text-sm font-medium text-text dark:text-gray-100 whitespace-nowrap">
        {weekLabel}
      </span>
      <button onClick={() => setWeekOffset(o => o + 1)} className={btnCls} aria-label="Next week">
        Next →
      </button>
      <button onClick={() => setWeekOffset(0)} className={btnCls}>
        Today
      </button>
      <label className="text-xs text-muted dark:text-gray-400 whitespace-nowrap">
        Jump to week of:
      </label>
      <input
        type="date"
        className={`${btnCls} cursor-pointer`}
        onChange={e => handleJumpDate(e.target.value)}
      />
      <button
        onClick={() => load(true)}
        className={`${btnCls} font-bold tap-sq`}
        aria-label="Refresh"
        title="Refresh data"
      >
        ↻
      </button>
    </div>
  );

  // ── All tab filter bar ────────────────────────────────────────────────────
  const filterBar = (
    <div className="px-3 sm:px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 space-y-2">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[160px] sm:flex-none sm:w-[200px]">
          <input
            type="text"
            placeholder="Search activity name or ID..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="w-full border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1.5 pr-6 text-xs bg-white dark:bg-gray-800 text-text dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent tap"
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-muted dark:text-gray-400 hover:text-text dark:hover:text-gray-200 text-base leading-none"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-text dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent tap"
        >
          <option value="all">All statuses</option>
          <option value="not started">Not Started</option>
          <option value="in progress">In Progress</option>
          <option value="complete">Complete</option>
          <option value="critical">Critical only</option>
        </select>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageShell className="pt-4 pb-10">

      {/* Shared page container — same edges as every other page. */}
      {/* Tab bar */}
      {tabBar}

      {/* Project switcher */}
      {projectSwitcher}

      {loading ? (
        <div className="px-4 mt-4 space-y-3">{[1, 2, 3].map(i => <SkeletonCard key={i} />)}</div>
      ) : error ? (
        <ErrorMessage message={error} onRetry={load} />
      ) : (
        <>
          {tab === 'week' && (
            <>
              {weekNavBar}
              <HealthBar
                weekActivities={[...startingSection, ...ongoingSection, ...finishingBase]}
                allActivities={activities}
                project={project}
                weekStartStr={weekStartStr}
                weekEndStr={weekEndStr}
                todayStr={todayStr}
              />
              {thresholds.is_distressed && (
                <div className="bg-watch-bg border-b border-watch-border/30 px-4 py-1.5 text-xs text-watch-text font-medium">
                  ⚠ Adjusted thresholds for this project — Critical: &lt;{thresholds.critical}d · Watch: &lt;{thresholds.watch}d · Healthy: &gt;{thresholds.watch}d
                </div>
              )}
              {isWeekEmpty ? (
                <div className="text-center py-16 px-4">
                  <div className="text-5xl mb-3">📅</div>
                  <p className="text-base font-semibold text-text dark:text-gray-100">Nothing scheduled this week</p>
                  <p className="text-sm text-muted dark:text-gray-400 mt-1">Use the arrows to navigate to another week.</p>
                </div>
              ) : (
                <WeeklyGrid
                  activities={[]}
                  tab="week"
                  expandedId={expandedId}
                  onToggle={handleToggle}
                  flashId={flashId}
                  userEmail={userEmail || ''}
                  projectId={project_id || ''}
                  activityNames={activityNames}
                  onSubmitSuccess={handleSubmitSuccess}
                  onSubmitError={handleSubmitError}
                  onSubmitStart={handleSubmitStart}
                  onBatchSubmitSuccess={handleBatchSubmitSuccess}
                  sections={sections}
                  thresholds={thresholds}
                />
              )}
            </>
          )}

          {tab === 'all' && (
            <>
              {filterBar}
              {filteredAll.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div className="text-5xl mb-3">📋</div>
                  <p className="text-base font-semibold text-text dark:text-gray-100">No activities match your filters</p>
                  <p className="text-sm text-muted dark:text-gray-400 mt-1">Try adjusting the search or filter criteria.</p>
                </div>
              ) : (
                <WeeklyGrid
                  activities={filteredAll}
                  tab="all"
                  expandedId={expandedId}
                  onToggle={handleToggle}
                  flashId={flashId}
                  userEmail={userEmail || ''}
                  projectId={project_id || ''}
                  activityNames={activityNames}
                  onSubmitSuccess={handleSubmitSuccess}
                  onSubmitError={handleSubmitError}
                  onSubmitStart={handleSubmitStart}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              )}
            </>
          )}

          {tab === 'floatpath' && (
            <FloatPathGrid
              activities={activities}
              thresholds={thresholds}
              project={project}
            />
          )}
        </>
      )}

      

      {savePopup && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-colors ${
            savePopup.status === 'saving' ? 'bg-black/10' : 'pointer-events-none'
          }`}
        >
          <div
            className={`mx-4 max-w-sm flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl text-sm font-semibold animate-fade-in ${
              savePopup.status === 'error'
                ? 'bg-crit-text text-white'
                : savePopup.status === 'saving'
                ? 'bg-white dark:bg-gray-800 text-text dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                : 'bg-accent text-white'
            }`}
          >
            {savePopup.status === 'saving' && (
              <div className="w-4 h-4 border-2 border-accent dark:border-green-400 border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            {savePopup.status === 'success' && (
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
            {savePopup.status === 'error' && (
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12.5" strokeLinecap="round" />
                <line x1="12" y1="16" x2="12" y2="16" strokeLinecap="round" />
              </svg>
            )}
            <span>{savePopup.message}</span>
          </div>
        </div>
      )}

      {/* Batch-save summary popup (Weekly Lookahead's multi-edit Save only) — dismissible via
          the × button, does not auto-dismiss and does not block the rest of the page, since
          it may carry a completion-date shift and a newly-critical list worth reading. */}
      {batchSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none px-4">
        <div className="pointer-events-auto w-full max-w-md max-h-[calc(100dvh-32px)] flex flex-col bg-ground dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl animate-fade-in overflow-hidden">
          {/* Header bar — dark green, matches the app's tab/section-header treatment. */}
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-ink shrink-0">
            <div className="flex items-center gap-2.5">
              <svg className="w-5 h-5 text-white shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 15l2 2 4.5-4.5" />
              </svg>
              <span className="text-lg font-semibold text-white">
                {batchSummary.count} {batchSummary.count === 1 ? 'activity' : 'activities'} updated
              </span>
            </div>
            <button
              onClick={closeBatchSummary}
              aria-label="Dismiss"
              className="text-white/80 hover:text-white text-2xl leading-none shrink-0 w-11 h-11 -mr-2 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          <div className="px-4 sm:px-6 py-5 space-y-4 overflow-y-auto">
            {/* Project completion — only rendered when this save actually moved it. */}
            {batchSummary.completionChanged && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted dark:text-gray-500 mb-2">
                  Project Completion
                </p>
                <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-[11px] text-muted dark:text-gray-500">Previous</p>
                    <p className="text-base font-semibold text-muted dark:text-gray-400">{formatDate(batchSummary.completionBefore)}</p>
                  </div>
                  <svg className="w-4 h-4 text-muted dark:text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l6 6-6 6M5 12h14" />
                  </svg>
                  <div>
                    <p className="text-[11px] text-muted dark:text-gray-500">Updated</p>
                    <p className="text-base font-bold text-text dark:text-gray-100">{formatDate(batchSummary.completionAfter)}</p>
                  </div>
                  <span className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${
                    batchSummary.daysDiff > 0 ? 'bg-crit-bg text-crit-text' : 'bg-tint text-accent dark:bg-green-900/30 dark:text-green-400'
                  }`}>
                    {batchSummary.daysDiff > 0 ? '+' : ''}{batchSummary.daysDiff}d
                  </span>
                </div>
              </div>
            )}

            {/* Critical activities section — ALWAYS shown, regardless of whether this save
                changed anything: it reports the project's current critical-path state, not
                just what shifted from this save. Zero-critical is the only case that swaps
                the list out for a plain positive line. */}
            {batchSummary.criticalPath.length === 0 ? (
              <div className="flex items-center gap-2.5 bg-tint dark:bg-green-900/20 rounded-xl px-4 py-3">
                <svg className="w-4 h-4 text-accent dark:text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-bold text-accent dark:text-green-400">
                  No critical activities
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2.5 bg-crit-bg rounded-xl px-4 py-3">
                  <svg className="w-4 h-4 text-crit-text shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86l-8.18 14.02A1 1 0 0 0 3 19.5h18a1 1 0 0 0 .89-1.62L13.71 3.86a1 1 0 0 0-1.72 0z" />
                  </svg>
                  <span className="text-sm font-bold text-crit-text">
                    {batchSummary.criticalPath.length} {batchSummary.criticalPath.length === 1 ? 'activity is' : 'activities are'} critical
                  </span>
                </div>

                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted dark:text-gray-500 mb-2">
                    Critical Path
                  </p>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    {batchSummary.criticalPath.slice(0, 6).map((a, i) => (
                      <button
                        key={a.activity_id}
                        onClick={() => handleCriticalActivityClick(a.activity_id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 min-h-[44px] text-left hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors ${
                          i > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''
                        }`}
                      >
                        <span className="text-sm text-muted dark:text-gray-400 font-mono shrink-0">{a.activity_id}</span>
                        <span className="text-sm text-text dark:text-gray-100 flex-1 truncate">{a.activity_name}</span>
                        <svg className="w-4 h-4 text-muted dark:text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Overflow link — only when there are more critical activities than shown
                    above. N is the FULL critical path count, matching exactly what the All
                    Activities "Critical only" filter will show. */}
                {batchSummary.criticalPath.length > 6 && (
                  <button
                    onClick={handleViewAllCritical}
                    className="flex items-center gap-1.5 text-sm font-semibold text-accent dark:text-green-400 hover:underline"
                  >
                    View all {batchSummary.criticalPath.length} critical activities
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l6 6-6 6M5 12h14" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        </div>
      )}
    </PageShell>
  );
};

export default Weekly;
